import React, { useState, useEffect } from "react";
import {
  Box,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  Radio,
  Grid,
  Divider,
  FormHelperText
} from "@material-ui/core";
import { IntroText, FormActionDrawer, ButtonLoader } from "../../../components";
import { useHeader } from "../../../contexts";
import { Form, Field, useFormState } from "react-final-form";
import createDecorator from "final-form-focus";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";
import { SERVICE_FLU_SHOT } from "../../../constants";
import { useTranslation } from "react-i18next";

const focusOnErrors = createDecorator();

const validationSchema = object({
  egg_allergy: string()
    .required()
    .label("This"),
  life_threatening_reaction: string()
    .required()
    .label("This"),
  guillain_barre_syndrome: string()
    .required()
    .label("This"),
  severely_ill: string()
    .required()
    .label("This")
});

const OptionsData = [
  {
    value: "egg_allergy",
    label_translation_key: "schedule.influenza_screening.options.egg_allergy"
  },
  {
    value: "life_threatening_reaction",
    label_translation_key:
      "schedule.influenza_screening.options.life_threatening_reaction"
  },
  {
    value: "guillain_barre_syndrome",
    label_translation_key:
      "schedule.influenza_screening.options.guillain_barre_syndrome"
  },
  {
    value: "severely_ill",
    label_translation_key: "schedule.influenza_screening.options.severely_ill"
  }
];

const IneligibleNotice = ({
  canContinue = false,
  scheduleData,
  updateScheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const formState = useFormState();

  const handleContinue = () => {
    const { values: influenzaScreening } = formState;

    // Get service selection
    const { serviceSelection = {} } = scheduleData;

    // Remove flu shot from service selection and add screening data
    updateScheduleData({
      influenzaScreening,
      serviceSelection: {
        ...serviceSelection,
        [SERVICE_FLU_SHOT]: false
      }
    });
  };

  return (
    <Box data-cy="schedule-screen-influenza-ineligible">
      <IntroText>{t("schedule.influenza_ineligible.intro_text")}</IntroText>
      {canContinue && (
        <ButtonLoader
          variant="contained"
          fullWidth
          color="primary"
          onClick={e => handleContinue()}
          data-cy="schedule-screen-influenza-ineligible-continue"
          loading={isSubmitting}
        >
          {t("schedule.influenza_ineligible.button_continue")}
        </ButtonLoader>
      )}
    </Box>
  );
};

const ScheduleInfluenzaScreening = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const { validate } = useYupValidation();
  const { setTitle } = useHeader();
  const [ineligible, setIneligible] = useState(false);
  const [ineligibleContinue, setIneligibleContinue] = useState(false);

  useEffect(() => {
    setTitle(t("schedule.influenza_screening.title"));
  }, []);

  /**
   * Determine if user is eligible
   *
   * @param {object} answers
   */
  const isEligible = answers => {
    const disqalifiyingAnswers = Object.keys(answers).filter(
      key => answers[key] === "true"
    );

    return disqalifiyingAnswers.length > 0 ? false : true;
  };

  /**
   * Determine if user can continue after ineligibility
   */
  const canContinueAfterIneligibility = () => {
    const { serviceSelection } = scheduleData;

    const otherServices = Object.keys(serviceSelection).filter(key =>
      key !== SERVICE_FLU_SHOT && serviceSelection[key] === true ? true : false
    );

    return otherServices.length > 0 ? true : false;
  };

  /**
   * Handle form validation and submit
   *
   * @param {*} data
   */
  const validateForm = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    if (isEligible(data)) {
      updateScheduleData({ influenzaScreening: data });
    } else {
      setIneligible(true);

      if (canContinueAfterIneligibility()) {
        setIneligibleContinue(true);
      }
    }
  };

  return (
    <>
      <Form
        onSubmit={validateForm}
        initialValues={{ ...scheduleData.influenzaScreening }}
        decorators={[focusOnErrors]}
        render={({ handleSubmit, submitErrors: errors = {} }) => (
          <>
            {ineligible ? (
              <IneligibleNotice
                canContinue={ineligibleContinue}
                scheduleData={scheduleData}
                updateScheduleData={updateScheduleData}
                isSubmitting={isSubmitting}
              />
            ) : (
              <form
                data-cy="schedule-screen-influenza-screening"
                onSubmit={handleSubmit}
              >
                <IntroText>
                  {t("schedule.influenza_screening.intro_text")}
                </IntroText>
                {OptionsData.map((option, index) => (
                  <Field
                    key={`radio-${index}`}
                    name={option.value}
                    type="radio"
                    render={({ input }) => (
                      <FormControl
                        component="fieldset"
                        error={!!errors[option.value]}
                      >
                        <Box py={1}>
                          <Grid
                            container
                            spacing={2}
                            justify="space-between"
                            alignItems="center"
                          >
                            <Grid item xs>
                              <FormLabel component="legend">
                                {t(option.label_translation_key)}
                              </FormLabel>
                            </Grid>
                            <Grid item>
                              <RadioGroup row {...input}>
                                <FormControlLabel
                                  value="false"
                                  control={<Radio color="primary" />}
                                  label={t(
                                    "schedule.influenza_screening.options.no"
                                  )}
                                  labelPlacement="start"
                                />
                                <FormControlLabel
                                  value="true"
                                  control={<Radio color="primary" />}
                                  label={t(
                                    "schedule.influenza_screening.options.yes"
                                  )}
                                  labelPlacement="start"
                                />
                              </RadioGroup>
                            </Grid>
                          </Grid>
                          {errors[option.value] && (
                            <FormHelperText c>
                              {errors[option.value]}
                            </FormHelperText>
                          )}
                        </Box>
                        <Divider />
                      </FormControl>
                    )}
                  />
                ))}
                <FormActionDrawer
                  setDrawerClearance={setDrawerClearance}
                  loading={isSubmitting}
                />
              </form>
            )}
          </>
        )}
      />
    </>
  );
};

export { ScheduleInfluenzaScreening };
